import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, CardMedia, Input, CircularProgress, Select, MenuItem } from "@mui/material";
import SwitchToggle from "../components/SwitchToggle";
import {
  useAddCategoryMutation,
  useUpdateCategoryMutation,
  useUploadMediaMutation
} from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  categoryData?: any;
  defaultType?: number;
}

export default function AddCategory({
  open,
  onClose,
  setOpen,
  categoryData,
  defaultType,
}: ModalProps) {
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [image, setImage] = useState("");
  const [isPopular, setIsPopular] = useState(false);
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<number[]>([]);
  const [errors, setErrors] = useState<any>({});

  const [addCategory, { isLoading: isAdding }] = useAddCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const [uploadMedia] = useUploadMediaMutation();

  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (open) {
      if (categoryData) {
        setName(categoryData.name || "");
        setNameAr(categoryData.name_ar || "");
        setImage(categoryData.image || "");
        setIsPopular(categoryData.isPopular === "true" || categoryData.isPopular === true);
        const vType = categoryData.type;
        setSelectedVehicleTypes(Array.isArray(vType) ? vType : (vType ? [vType] : []));
        setErrors({});
      } else {
        setName("");
        setNameAr("");
        setImage("");
        setIsPopular(false);
        setSelectedVehicleTypes(defaultType ? [defaultType] : []);
        setErrors({});
      }
    }
  }, [open, categoryData, defaultType]);

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];

      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format not supported (Only JPG, JPEG, and PNG)");
        return;
      }

      const previewUrl = URL.createObjectURL(file);

      // Set local preview immediately
      setImage(previewUrl);

      const formData = new FormData();
      formData.append("file", file);

      setIsUploadingImage(true);

      try {
        const res = await uploadMedia(formData).unwrap();
        const uploadedUrl = res?.data?.url || res?.url || res?.data;

        if (uploadedUrl) {
          setImage(uploadedUrl);
          toast.success("File uploaded successfully");
        }
      } catch (err: any) {
        toast.error(err?.data?.message || "File upload failed");
        setImage("");
      } finally {
        setIsUploadingImage(false);
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Category name is required";
      isValid = false;
    } else if (name.length > 40) {
      tempErrors.name = "Category Name cannot exceed 40 characters";
      isValid = false;
    }

    if (nameAr.trim() && nameAr.length > 40) {
      tempErrors.nameAr = "اسم الفئة cannot exceed 40 characters";
      isValid = false;
    }

    if (selectedVehicleTypes.length === 0) {
      tempErrors.vehicleType = "At least one vehicle type is required";
      isValid = false;
    }

    setErrors(tempErrors);
    if (!isValid) return;

    const payload: any = {
      name,
      name_ar: nameAr,
      isPopular,
      order: categoryData?.order || 1,
      type: selectedVehicleTypes,
    };

    if (image) {
      payload.image = image;
      payload.icon = image;
    }

    try {
      if (categoryData?._id) {
        const res = await updateCategory({ id: categoryData._id, ...payload }).unwrap();
        toast.success(res.message || "Category updated successfully");
      } else {
        const res = await addCategory(payload).unwrap();
        toast.success(res.message || "Category added successfully");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog" style={{ maxWidth: 700, width: "100%" }}>
        <div className="modal-body">
          {/* MODAL TITLE */}
          <div className="modal_title">
            <h2>{categoryData ? "Edit Category (تحرير الفئة)" : "Add Category (أضف فئة)"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={handleSave}>
            <div className="gap_p">
              {/* UPLOAD IMAGE */}
              <div className="control_group">
                <label htmlFor="upload-category-image">
                  Upload Image (تحميل الصورة)
                </label>

                {image ? (
                  <div className="upload_image no_radius">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={image} alt="Category" />
                      </figure>
                      <CloseIcon
                        className="remove_img"
                        onClick={() => setImage("")}
                      />
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image no_radius"
                    htmlFor="upload-category-image"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="upload-category-image"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleFileUpload(e)}
                      disabled={isUploadingImage}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        {isUploadingImage ? <CircularProgress size={24} /> : (
                          <>
                            <img src="/static/images/placeholder.png" alt="Upload" />
                            <span className="upload_img_icon">
                              <img src="/static/images/edit2_icon.svg" alt="Edit" />
                            </span>
                          </>
                        )}
                      </figure>
                    </span>
                  </label>
                )}
              </div>


              {/* CATEGORY NAME + CATEGORY NAME (ARABIC) */}
              <div style={{ display: "flex", gap: 16, width: "100%" }}>
                <div className="control_group" style={{ flex: 1 }}>
                  <label htmlFor="category-name">Category Name</label>
                  <TextField
                    id="category-name"
                    placeholder="Enter Name"
                    fullWidth
                    hiddenLabel
                    value={name}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (input.length > 0) {
                        setName(input.charAt(0).toUpperCase() + input.slice(1));
                      } else {
                        setName(input);
                      }
                      if (errors.name) setErrors({ ...errors, name: "" });
                    }}
                    error={!!errors.name}
                    helperText={errors.name}
                    inputProps={{ maxLength: 40 }}
                  />
                </div>

                <div className="control_group" style={{ flex: 1 }}>
                  <label htmlFor="category-name-ar">اسم الفئة</label>
                  <TextField
                    id="category-name-ar"
                    placeholder="أدخل الاسم"
                    fullWidth
                    hiddenLabel
                    value={nameAr}
                    onChange={(e) => {
                      setNameAr(e.target.value);
                      if (errors.nameAr) setErrors({ ...errors, nameAr: "" });
                    }}
                    error={!!errors.nameAr}
                    helperText={errors.nameAr}
                    inputProps={{ maxLength: 40 }}
                  />
                </div>
              </div>

              {/* VEHICLE TYPE */}
              <div className="control_group">
                <label htmlFor="VehicleType">Vehicle Type (نوع المركبة)</label>
                <Select
                  multiple
                  fullWidth
                  hiddenLabel
                  displayEmpty
                  value={selectedVehicleTypes}
                  onChange={(event) => {
                    const value = event.target.value as number[];
                    setSelectedVehicleTypes(value);
                    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
                  }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "Select Vehicle Type";
                    return selected.map(val =>
                      getVehicleTypeLabel(val)
                    ).join(", ");
                  }}
                  error={!!errors.vehicleType}
                  disabled={!!defaultType}
                >
                  <MenuItem value="" disabled>Select Vehicle Type</MenuItem>
                  {Object.keys(VEHICLE_TYPE)
                    .filter(k => isNaN(Number(k)))
                    .map((key) => (
                      <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                        {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                      </MenuItem>
                    ))}
                </Select>
                {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
              </div>

              {/* Popular */}
              <div className="control_group flex w_100">
                <label>Is this Popular Category? (هل هذه فئة شائعة؟)</label>
                <SwitchToggle
                  checked={isPopular}
                  onChange={(e) => setIsPopular(e.target.checked)}
                />
              </div>
            </div>

            {/* FOOTER BUTTON */}
            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating || isUploadingImage}
              >
                {isAdding || isUpdating ? "Saving... (توفير...)" : "Save (يحفظ)"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
